import { forwardRef, Ref, useImperativeHandle, useMemo, useState } from 'react'
import { Dimensions, FlatList, Platform } from 'react-native'
import type { ColorOptions } from '../../theme/UiThemeProvider'
import { getOS, isSafari } from '../../utils/utils'
import { Avtaar } from '../Avtaar'
import { Box, BoxProps } from '../Box'
import { Icon } from '../Icon'
import { MaterialTextInput } from '../MaterialTextInput'
import { ModalDialog, ModalTypes } from '../Modal'
import { SearchBar } from '../SearchBar'
import { Spacer } from '../Spacer'
import { Text } from '../Text'
import { TextInput } from '../TextInput'

export interface DropDownData {
  text?: string
  imageUrl?: string
}

export interface DropdownProps {
  ID?: string
  error?: string
  editable?: boolean
  data: Array<DropDownData>
  onChange?: (value: string) => void
  touched?: boolean
  value?: string
  label?: string
  placeHolder?: string
  isModalSizeFixed?: boolean
  modalPosition?: keyof typeof ModalTypes
  getSelectedItemData?: (value: any) => void
  type?: 'Material' | 'Normal'
  testID?: string
  nonEditableTextColor?: ColorOptions
  showDownArrow?: boolean
  dropdownIconSize: number
  dropdownIconColor: ColorOptions
  bottomLineColor: ColorOptions
  tintColor: ColorOptions
  fontFamily?: string
  animatedColorValue?: string
  placeholderTextColor?: ColorOptions
  dropdownTextColor?: ColorOptions
  dropdownHeaderColor?: ColorOptions
  fontSize?: number
}

export interface RefCalback {
  isEditable(): boolean
  focus(): void
}

const MIN_DATA_TO_SHOW = 10

const getOSType = getOS()

export const Dropdown = forwardRef((props: DropdownProps, ref: Ref<RefCalback>) => {
  const {
    data,
    touched,
    editable = true,
    isModalSizeFixed = false,
    value,
    error,
    onChange,
    label,
    placeHolder = `Select ${props.label}`,
    modalPosition = 'Bottom',
    getSelectedItemData,
    type = 'Normal',
    testID,
    nonEditableTextColor,
    showDownArrow = true,
    dropdownIconSize = 14,
    dropdownIconColor = 'PRIMARY',
    bottomLineColor,
    dropdownTextColor = 'BLACK_75',
    tintColor,
    fontFamily,
    fontSize,
    animatedColorValue,
    placeholderTextColor = 'SECONDARY_GREY',
    dropdownHeaderColor,
  } = props
  let modifiedValue = value
  let isPlaceHolder = false
  if (type === 'Normal' && !modifiedValue) {
    modifiedValue = placeHolder
    isPlaceHolder = true
  }

  const isLargeScreen = useMemo(() => Dimensions.get('window').width > 550, [])

  const [errors, setErrors] = useState('')
  const [isTouched, setIsTouched] = useState(false)
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')

  useImperativeHandle(ref, () => ({
    isEditable: () => {
      return true
    },
    focus: () => handleOpen(true),
  }))

  const handleOpen = (isOpen: boolean) => {
    // If the list length is more than zero then only will show modail
    if (data.length > 0) {
      setOpen(isOpen)
    } else if (data.length === 0 && error) {
      // If the list length is zero then showing error message
      setErrors(error)
      setIsTouched(true)
    }
  }

  const onHandlePress = (item: DropDownData) => {
    if (onChange) {
      // Once we selected the item and comeback to drown we need to clear the serch text
      setSearchText('')
      onChange(item?.text ?? '')
    }
    if (getSelectedItemData && item) {
      getSelectedItemData(item)
    }

    handleOpen(false)
  }

  const getListData = () => {
    const searchfn = (item: DropDownData) => {
      const search = searchText.toLowerCase()
      const text = item?.text?.toLowerCase() ?? ''
      return text.includes(search)
    }
    const listData = data.filter(searchfn)
    return listData
  }

  const onChangeText = (text: string) => {
    setSearchText(text)
  }

  const renderItem = ({ item, index }: { item: any; index: number }) => {
    const handlePress = () => onHandlePress(item)
    const title = item?.text ?? ''

    return (
      <Box
        {...styles.container}
        onPress={handlePress}
        underlayColor="GREY_SEPARATOR"
        testID={`dropdownListItemId${index}`}>
        <>
          <Avtaar
            hide={!item?.imageUrl}
            fontFamily={fontFamily}
            source={item?.imageUrl ?? ''}
            type="medium"
            title={title}
          />
          <Box padding={8} flexShrink={1}>
            <Text fontWeight="700" color={dropdownTextColor} fontFamily={fontFamily}>
              {title}
            </Text>
          </Box>
        </>
      </Box>
    )
  }

  const rightComponent = showDownArrow && <Icon id="ARROW_DOWN" color="PRIMARY" />

  const renderEmptyView = () => {
    return (
      <Box {...styles.emptyContainer(isLargeScreen)}>
        <Text fontFamily={fontFamily}> Search not found </Text>
      </Box>
    )
  }
  return (
    <Box
      onPress={() => {
        if (editable) {
          handleOpen(true)
        }
      }}
      underlayColor={'TRANSPARENT'}
      width={'100%'}
      pointerEvents={Platform.OS === 'web' ? 'box-only' : 'auto'}
      testID={'dropdownId'}>
      {type === 'Normal' && (
        <TextInput
          fontFamily={fontFamily}
          testID={testID}
          label={label}
          useText={true}
          onPress={() => {
            if (editable) {
              handleOpen(true)
            }
          }}
          placeholderTextColor={placeholderTextColor}
          error={error || errors} // Will update the errors value if the list size zero
          touched={touched || isTouched} // Will update the isTouched value if the list size zero
          editable={false}
          fontSize={fontSize}
          isStartSelection={true}
          numberOfLines={1}
          value={modifiedValue}
          color={editable ? (isPlaceHolder ? placeholderTextColor : 'BLACK_75') : nonEditableTextColor}
          rightComponent={rightComponent}
        />
      )}
      {type === 'Material' && (
        <MaterialTextInput
          fontFamily={fontFamily}
          label={label}
          error={error || errors} // Will update the errors value if the list size zero
          touched={touched || isTouched} // Will update the isTouched value if the list size zero
          editable={false}
          numberOfLines={1}
          value={modifiedValue}
          color={editable ? (isPlaceHolder ? placeholderTextColor : 'BLACK_75') : nonEditableTextColor}
          rightComponent={rightComponent}
          onPressOut={() => {
            if (editable) {
              handleOpen(true)
            }
          }}
          tintColor={tintColor}
          animatedColorValue={animatedColorValue}
          placeholderTextColor={placeholderTextColor}
        />
      )}
      <ModalDialog
        isFromDropDown
        type={modalPosition}
        title={label ?? ''}
        show={open}
        onDismiss={() => handleOpen(false)}>
        <Box
          padding={8}
          bg="WHITE"
          {...(isModalSizeFixed
            ? styles.boxContainer
            : { maxHeight: '90%', height: '90vh', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 })}
          f={isSafari() ? 1 : undefined}>
          <Spacer size={8} />
          <Box paddingHorizontal={8}>
            <SearchBar
              fontFamily={fontFamily}
              hide={data.length <= MIN_DATA_TO_SHOW}
              onChange={onChangeText}
              placeholder="Search using name"
              value={searchText}
            />
          </Box>
          <Box paddingVertical={8} height={data.length > MIN_DATA_TO_SHOW ? '90%' : undefined} maxHeight={'90%'}>
            <FlatList
              nativeID={getOSType === 'Windows' ? 'showListScrollBar' : ''}
              keyboardShouldPersistTaps="always"
              keyboardDismissMode="on-drag"
              data={getListData()}
              keyExtractor={(_, index) => index.toString()}
              renderItem={renderItem}
              ListEmptyComponent={renderEmptyView}
            />
          </Box>
        </Box>
      </ModalDialog>
    </Box>
  )
})

const styles = {
  container: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
  } as BoxProps,
  boxContainer: {
    height: '90vh',
    width: ['80vw', '30vw'],
  } as BoxProps,
  emptyContainer: (isLargeScreen: boolean) =>
    ({
      height: isLargeScreen ? '70vh' : undefined,
      wight: isLargeScreen ? '50vw' : '100%',
      justifyContent: 'center',
      alignItems: 'center',
    }) as BoxProps,
}
